import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import Cookies from 'js-cookie'
import { http } from 'interfaces'

export const ProjectModule = {
  moduleIdByName: new Map<string, number>([
    ['Organizations', 1],
    ['People', 2],
    ['Positions', 3]
  ]),

  getId (moduleName: string): number | undefined {
    return this.moduleIdByName.get(moduleName)
  }
}

export enum SessionRegion {
  US = 'us',
  INTL = 'international'
}

export enum AccessLevel {
  VIEWER = 'viewer',
  EDITOR = 'editor',
  MANAGER = 'manager'
}

export type MemberNavSort = 'last_name' | 'district'
export type LeadershipNavSort = 'display_order' | 'last_name'
export type NameMatchMode = 'contains' | 'starts_with'

export const MEMBER_NAV_SORT_DEFAULT: MemberNavSort = 'last_name'
export const LEADERSHIP_NAV_SORT_DEFAULT: LeadershipNavSort = 'display_order'
export const NAME_MATCH_MODE_DEFAULT: NameMatchMode = 'contains'

interface SessionData {
  email: string
  first_name: string
  last_name: string
  username: string
  module_name: string
  uniqueId: string
  newTab: boolean
  keepDisabledTextBlack: boolean
  accessLevel: AccessLevel
  region: 'international' | 'us'
  memberNavSortBy: MemberNavSort
  leadershipNavSortBy: LeadershipNavSort
  peopleNameMatchMode: NameMatchMode
}

interface SessionContextData {
  sessionData: SessionData
  setSessionData: (data: any) => void
  logout: () => void
  login: (userData: any) => void
  isAuthenticated: () => Promise<boolean>
  changeModuleName: (newName: string) => void
  changeUniqueId: (newName: string) => void
  changeRegion: (newRegion: 'us' | 'international') => void
  openNewTab: (link: string, focus?: boolean) => void
  changeNewTab: (newTabValue: boolean) => void
  changeKeepDisabledTextBlack: (next: boolean) => void
  changeMemberNavSortBy: (next: MemberNavSort) => void
  changeLeadershipNavSortBy: (next: LeadershipNavSort) => void
  changePeopleNameMatchMode: (next: NameMatchMode) => void
  hasAccessLevel: (requiredLevel: AccessLevel) => boolean
}

const SessionContext = createContext<SessionContextData | undefined>(undefined)

export const useSessionContext = (): SessionContextData => {
  const context = useContext(SessionContext)
  if (context == null) {
    throw new Error('useSessionContext must be used within a SessionProvider')
  }
  return context
}

export const SessionProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [sessionData, setSessionData] = useState<SessionData>(Cookies.get('sessiondata') != null ? JSON.parse(Cookies.get('sessiondata') as string) : {})
  const inFlightAuthCheck = useRef<Promise<boolean> | null>(null)

  const getOldSessionData = (): Partial<SessionData> => {
    const oldSessionCookie = Cookies.get('sessiondata')
    if (oldSessionCookie != null) {
      const oldSessionData = JSON.parse(oldSessionCookie) as SessionData
      return {
        region: oldSessionData.region,
        newTab: oldSessionData.newTab,
        keepDisabledTextBlack: oldSessionData.keepDisabledTextBlack,
        memberNavSortBy: oldSessionData.memberNavSortBy,
        leadershipNavSortBy: oldSessionData.leadershipNavSortBy,
        peopleNameMatchMode: oldSessionData.peopleNameMatchMode
      }
    }
    return {}
  }
  const persistSessionDefaults = (): void => {
    const oldSessionData = getOldSessionData()
    if (sessionData.region == null) {
      sessionData.region = oldSessionData.region ?? SessionRegion.INTL
    }
    if (sessionData.newTab == null) {
      sessionData.newTab = oldSessionData.newTab ?? true
    }
    if (sessionData.keepDisabledTextBlack == null) {
      sessionData.keepDisabledTextBlack = oldSessionData.keepDisabledTextBlack ?? false
    }
    if (sessionData.memberNavSortBy == null) {
      sessionData.memberNavSortBy = oldSessionData.memberNavSortBy ?? MEMBER_NAV_SORT_DEFAULT
    }
    if (sessionData.leadershipNavSortBy == null) {
      sessionData.leadershipNavSortBy = oldSessionData.leadershipNavSortBy ?? LEADERSHIP_NAV_SORT_DEFAULT
    }
    if (sessionData.peopleNameMatchMode == null) {
      sessionData.peopleNameMatchMode = oldSessionData.peopleNameMatchMode ?? NAME_MATCH_MODE_DEFAULT
    }
    Cookies.set('sessiondata', JSON.stringify(sessionData), { expires: 14, sameSite: 'Strict' })
    localStorage.setItem('newTab', JSON.stringify(sessionData.newTab))
  }

  useEffect(() => {
    persistSessionDefaults()
  }, [sessionData])

  useEffect(() => {
    if (sessionData.username == null) {
      void isAuthenticated()
    }
  }, [])

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent): void => {
      if (event.key === 'newTab' && (event.newValue != null)) {
        const a = event.newValue
        setSessionData((oldData: SessionData) => ({
          ...oldData,
          newTab: JSON.parse(a)
        }))
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => { window.removeEventListener('storage', handleStorageChange) }
  }, [])

  const isAuthenticated = useCallback(async (): Promise<boolean> => {
    if (inFlightAuthCheck.current != null) {
      return await inFlightAuthCheck.current
    }
    const check = http.get('/check-auth/', { withCredentials: true }).then(response => {
      const authenticated = (Boolean(response.data.authenticated))
      if (authenticated && inFlightAuthCheck.current === check) {
        setSessionData((prevSessionData: SessionData) => ({
          ...prevSessionData,
          ...response.data.user_data
        }))
      }
      return authenticated
    }).catch(() => false).finally(() => {
      if (inFlightAuthCheck.current === check) {
        inFlightAuthCheck.current = null
      }
    })
    inFlightAuthCheck.current = check
    return await check
  }, [])

  const openNewTab = (link: string, focus: boolean = true): void => {
    if (!sessionData.newTab) {
      window.location.href = link
    } else {
      const newTab = window.open(link, '_blank')
      if (focus) newTab?.focus()
    }
  }

  const changeRegion = (newRegion: 'us' | 'international'): void => {
    if (newRegion !== sessionData.region) {
      setSessionData((oldData: SessionData) => ({
        ...oldData,
        region: newRegion
      }))
    }
  }

  const changeNewTab = (newTabValue: boolean): void => {
    if (newTabValue !== sessionData.newTab) {
      setSessionData((oldData: SessionData) => ({
        ...oldData,
        newTab: newTabValue
      }))
    }
  }

  const changeKeepDisabledTextBlack = (next: boolean): void => {
    if (next !== sessionData.keepDisabledTextBlack) {
      setSessionData((oldData: SessionData) => ({
        ...oldData,
        keepDisabledTextBlack: next
      }))
    }
  }

  const changeMemberNavSortBy = (next: MemberNavSort): void => {
    if (next !== sessionData.memberNavSortBy) {
      setSessionData((oldData: SessionData) => ({
        ...oldData,
        memberNavSortBy: next
      }))
    }
  }

  const changeLeadershipNavSortBy = (next: LeadershipNavSort): void => {
    if (next !== sessionData.leadershipNavSortBy) {
      setSessionData((oldData: SessionData) => ({
        ...oldData,
        leadershipNavSortBy: next
      }))
    }
  }

  const changePeopleNameMatchMode = (next: NameMatchMode): void => {
    if (next !== sessionData.peopleNameMatchMode) {
      setSessionData((oldData: SessionData) => ({
        ...oldData,
        peopleNameMatchMode: next
      }))
    }
  }

  const changeModuleName = (newName: string): void => {
    if (newName !== sessionData.module_name) {
      setSessionData((oldData: SessionData) => {
        const newData = Object.assign({}, oldData)
        newData.module_name = newName
        return newData
      })
    }
  }

  const changeUniqueId = (newUniqueId: string): void => {
    if (newUniqueId !== sessionData.uniqueId) {
      setSessionData((oldData: SessionData) => ({
        ...oldData,
        uniqueId: newUniqueId
      }))
      document.title = newUniqueId
    }
  }

  const login = (userData: SessionData): void => {
    setSessionData(userData)
  }

  const logout = (): void => {
    inFlightAuthCheck.current = null
    Cookies.remove('sessionid')
    Cookies.remove('sessiondata')
    setSessionData((prevState: SessionData) => ({
      ...prevState,
      email: '',
      first_name: '',
      last_name: '',
      username: '',
      module_name: '',
      uniqueId: '',
      newTab: prevState.newTab,
      region: prevState.region
    }))
  }

  const hasAccessLevel = (requiredLevel: AccessLevel): boolean => {
    const accessLevels = Object.values(AccessLevel)
    const userLevelIndex = accessLevels.indexOf(sessionData.accessLevel)
    const requiredLevelIndex = accessLevels.indexOf(requiredLevel)

    return userLevelIndex >= requiredLevelIndex
  }

  return (
        <SessionContext.Provider
            value={{ sessionData, setSessionData, logout, login, isAuthenticated, changeModuleName, changeUniqueId, changeRegion, openNewTab, changeNewTab, changeKeepDisabledTextBlack, changeMemberNavSortBy, changeLeadershipNavSortBy, changePeopleNameMatchMode, hasAccessLevel }}>
            {children}
        </SessionContext.Provider>
  )
}
